import { useState } from "preact/hooks";
import {
  DIM_DEFS,
  bucketDetail,
  bucketLabel,
  drill,
  sliceIsScoped,
  useFacet,
  useLift,
  type Bucket,
  type DimId,
} from "../../state/explore";
import { FacetSheet } from "./ExploreSheet";
import { ComposerPieces } from "./ComposerPieces";
import { exploreScope } from "../../state/store";

interface FacetPanelProps {
  dim: DimId;
  /** Overrides the catalogue title (the pairing panels: "Often programmed with…"). */
  title?: string;
  /** One line under the title. */
  hint?: string;
  /** Keys to hide — a pairing panel hides the entity it's scoped to. */
  exclude?: string[];
  /** Rows shown collapsed (a phone screen's worth). */
  rows?: number;
  /** Offer the "Distinctive" (lift) sort when the slice is scoped. */
  lens?: boolean;
  /** Rows to fetch (≤200). Small dims come back whole regardless. */
  limit?: number;
  /** Pieces under a composer filter: the composer's own pieces, or everyone else's. */
  own?: "only" | "others" | null;
  /** Drop the panel when the slice has at most one bucket — an organization's Cities
   *  panel reading "Vienna 100%" is a fact for the header, not a chart. */
  hideIfSingle?: boolean;
  onDrill?: () => void;
}

const COLLAPSED = 8;
const EXPANDED = 24;

/** One facet: a ranked bar list over the current slice. Tap a row to drill (it becomes a
 *  filter and every other panel re-aggregates); "More" grows the list in place; "All"
 *  opens the searchable full list as a sheet. Previous rows stay visible, dimmed, while a
 *  re-fetch is in flight — no blank flashes between taps. */
export function FacetPanel({ dim, title, hint, exclude, rows = COLLAPSED, lens = false, limit = 50, own = null, hideIfSingle = false, onDrill }: FacetPanelProps) {
  const def = DIM_DEFS[dim];
  const state = useFacet(dim, { limit, own });
  const [expanded, setExpanded] = useState(false);
  const [distinctive, setDistinctive] = useState(false);
  const [sheetOpen, setSheetOpen] = useState(false);
  // Composer rows expand in place to list that composer's pieces (one open at a time).
  const [openKey, setOpenKey] = useState<string | null>(null);

  const scoped = sliceIsScoped.value;
  const liftOn = lens && !!def.liftable && scoped && distinctive;
  const lifts = useLift(dim, state, liftOn);

  let buckets: Bucket[] = exclude?.length ? state.buckets.filter((b) => !exclude.includes(b.key)) : state.buckets;
  if (liftOn && lifts.size) {
    // Support floor: a 1-concert bucket can post a huge ratio — rank what's actually notable.
    buckets = [...buckets]
      .filter((b) => b.n >= 3 && lifts.has(b.key))
      .sort((a, b) => (lifts.get(b.key)!.lift - lifts.get(a.key)!.lift) || b.n - a.n);
  }

  const cap = def.small ? (expanded ? 200 : rows) : expanded ? EXPANDED : rows;
  const visible = buckets.slice(0, cap);
  const maxN = visible.reduce((m, b) => Math.max(m, b.n), 1);
  const hiddenCount = buckets.length - visible.length;
  const canSheet = !def.small && (state.truncated || buckets.length > cap);
  const loadingFresh = state.loading && state.buckets.length === 0;

  // Only worth saying when the archive could have contributed (past / all-time scope).
  const archiveInPlay = exploreScope.value !== "upcoming";
  const erasNote = !archiveInPlay
    ? null
    : state.eras.length === 1 && def.eras.length > 1 && !state.loading && state.matched > 0
      ? state.eras[0] === "live"
        ? `live listings only · shares of ${state.matched.toLocaleString()} concerts`
        : `archive only · shares of ${state.matched.toLocaleString()} concerts`
      : def.eras.length === 1 && def.eras[0] === "live" ? "live listings only" : null;

  function onRow(b: Bucket) {
    if (!def.drillable) return;
    if (drill(dim, b)) onDrill?.();
  }

  // "Amsterdam 100% · Eindhoven <1%" is one city for any reader's purposes.
  const effectivelySingle =
    state.buckets.length <= 1 ||
    (state.matched > 0 && state.buckets[0].n / state.matched >= 0.95);
  if (hideIfSingle && !state.loading && !state.error && effectivelySingle) return null;

  return (
    <section class={`xp-panel${state.loading ? " is-loading" : ""}`} aria-busy={state.loading}>
      <header class="xp-panel-head">
        <div class="xp-panel-titles">
          <h3 class="xp-panel-title">{title ?? def.title}</h3>
          {(hint || erasNote) && (
            <p class="xp-panel-hint">
              {hint}
              {hint && erasNote ? " · " : ""}
              {erasNote}
            </p>
          )}
        </div>
        {lens && def.liftable && scoped && state.buckets.length >= 4 && (
          <div class="xp-seg" role="group" aria-label="Sort">
            <button
              type="button"
              class={`xp-seg-btn${!distinctive ? " active" : ""}`}
              aria-pressed={!distinctive}
              onClick={() => setDistinctive(false)}
            >
              Most
            </button>
            <button
              type="button"
              class={`xp-seg-btn${distinctive ? " active" : ""}`}
              aria-pressed={distinctive}
              onClick={() => setDistinctive(true)}
              title="Ranked by how much more often this appears here than in concerts generally"
            >
              Distinctive
            </button>
          </div>
        )}
      </header>

      {state.error && <p class="xp-note xp-error">Couldn't load: {state.error}</p>}

      {loadingFresh && !state.error && (
        <ol class="xp-rows xp-skeleton" aria-hidden="true">
          {Array.from({ length: Math.min(rows, 6) }, (_, i) => (
            <li key={i} class="xp-row xp-row-skel">
              <span class="xp-skel-line" style={{ width: `${55 + ((i * 17) % 35)}%` }} />
            </li>
          ))}
        </ol>
      )}

      {!loadingFresh && !state.error && visible.length === 0 && (
        <p class="xp-note">
          {liftOn && state.buckets.length > 0
            ? "Nothing stands out yet — distinctive needs a few concerts per row."
            : "Nothing in this slice."}
        </p>
      )}

      {visible.length > 0 && (
        <ol class="xp-rows">
          {visible.map((b, i) => {
            const label = bucketLabel(dim, b);
            // A composer's own pieces don't need the composer under every row.
            const detail = own === "only" ? null : bucketDetail(dim, b);
            const lift = liftOn ? lifts.get(b.key) : undefined;
            const share = state.matched > 0 ? (b.n / state.matched) * 100 : 0;
            const inert = !def.drillable || b.key === "unknown" || b.key === "unclassified" || (dim === "alive" && b.key !== "living") || (dim === "premiere" && b.key !== "premiere");
            const expandable = dim === "composer" && !inert;
            const open = expandable && openKey === b.key;
            return (
              <li key={b.key} class={open ? "is-open" : undefined}>
                <div class="xp-rowline">
                  <button
                    type="button"
                    class={`xp-row${inert ? " is-inert" : ""}${dim === "piece" ? " xp-row-wrap" : ""}`}
                    onClick={() => onRow(b)}
                    disabled={inert}
                    title={inert ? undefined : `Narrow to ${label}`}
                  >
                    {!def.keyOrder && <span class="xp-rank">{i + 1}</span>}
                    <span class="xp-row-main">
                      <span class="xp-row-name">
                        {label}
                        {detail && <span class="xp-row-detail">{detail}</span>}
                      </span>
                      <span class="xp-bar-track">
                        <span class="xp-bar" style={{ width: `${Math.max(1.5, (b.n / maxN) * 100)}%` }} />
                      </span>
                    </span>
                    <span class="xp-row-num">
                      <span class="xp-row-n">{b.n.toLocaleString()}</span>
                      <span class="xp-row-sub">
                        {lift ? `×${lift.lift >= 10 ? lift.lift.toFixed(0) : lift.lift.toFixed(1)}` : share >= 1 ? `${Math.round(share)}%` : ""}
                      </span>
                    </span>
                  </button>
                  {expandable && (
                    <button
                      type="button"
                      class="xp-row-expand"
                      aria-expanded={open}
                      aria-label={open ? `Hide ${label}'s pieces` : `Show ${label}'s pieces`}
                      title={open ? "Hide pieces" : "Pieces"}
                      onClick={() => setOpenKey(open ? null : b.key)}
                    >
                      <svg viewBox="0 0 16 16" width="14" height="14" aria-hidden="true">
                        <path d="M4 6l4 4 4-4" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" />
                      </svg>
                    </button>
                  )}
                </div>
                {open && <ComposerPieces composerId={Number(b.key)} composerName={label} onDrill={onDrill} />}
              </li>
            );
          })}
        </ol>
      )}

      {(hiddenCount > 0 || canSheet || (expanded && buckets.length > rows)) && (
        <footer class="xp-panel-foot">
          {hiddenCount > 0 && !expanded && (
            <button type="button" class="xp-link-btn" onClick={() => setExpanded(true)}>
              Show {Math.min(hiddenCount, (def.small ? 200 : EXPANDED) - cap).toLocaleString()} more
            </button>
          )}
          {expanded && buckets.length > rows && (
            <button type="button" class="xp-link-btn" onClick={() => setExpanded(false)}>
              Show less
            </button>
          )}
          {canSheet && (
            <button type="button" class="xp-link-btn" onClick={() => setSheetOpen(true)}>
              All {def.title.toLowerCase()} &amp; search
            </button>
          )}
        </footer>
      )}

      {sheetOpen && (
        <FacetSheet
          dim={dim}
          title={title ?? def.title}
          exclude={exclude}
          own={own}
          onClose={() => setSheetOpen(false)}
          onDrill={() => {
            setSheetOpen(false);
            onDrill?.();
          }}
        />
      )}
    </section>
  );
}
